/**
 * Data hooks for the Engineering page.
 *
 *   useEngineering() — fetches GET /api/engineering and returns the validated
 *                      EngineeringResponse (the open lf2.0 GitHub issues, plus
 *                      the `configured` not-connected sentinel and the repo /
 *                      label the surface is bound to).
 *   useCreateIssue() — POST /api/engineering (create the inflow capture).
 *   useCloseIssue()  — POST /api/engineering/:number/close (mark done).
 *   useSetPinned()   — POST /api/engineering/:number/pin (toggle the float).
 *
 * Surface is system-wide — no viewer scoping (key has no viewer slot).
 *
 * Refetch posture: 5-minute interval. Issues move on the minutes-to-hours
 * scale, and Command Center is a window onto the tracker, not where issues are
 * edited — a faster poll is noise. Window-focus refetch via the global TanStack
 * default still catches the operator returning to the tab.
 *
 * Write reconciliation — optimistic, NOT refetch-on-success (this is load-
 * bearing). GitHub's filtered issues-list endpoint
 * (GET /repos/:repo/issues?labels=…&state=open) is eventually consistent: it
 * lags a write by a few seconds, so right after a write a brand-new issue is
 * not in the list yet and a just-closed one still is. An immediate refetch
 * therefore reads a STALE list — the created issue missing, the closed one
 * lingering — and with the 5-minute poll that wrong state sits on screen until
 * a manual reload (the bug the ADR-0110 live walk caught: a created issue only
 * appeared after a hard reset). So each write instead patches the cached list
 * from its own authoritative response (create/close return the projected
 * issue; pin carries the number + new pin state) and triggers NO refetch.
 * `setQueryData` bumps the query's freshness, so the global 30s staleTime
 * bridges the lag window without a focus-refetch clobbering the patch; the
 * 5-minute poll / a later focus reconciles once GitHub is consistent. The
 * patched state is already correct — there is nothing to "confirm," only
 * ordering/normalisation to re-sync, which is not user-visible.
 */

import {
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  EngineeringResponseSchema,
  ENGINEERING_PINNED_LABEL,
  type CreateIssueRequest,
  type EngineeringIssue,
  type EngineeringResponse,
} from "@command-center/shared";
import { apiPost } from "./api";
import { createPollingQuery } from "./create-polling-query";

const ENGINEERING_KEY = ["engineering"] as const;

export const useEngineering = createPollingQuery({
  queryKey: ENGINEERING_KEY,
  path: "/api/engineering",
  schema: EngineeringResponseSchema,
  refetchIntervalMs: 5 * 60_000,
});

/**
 * Patch the cached EngineeringResponse's issue list in place. Triggers NO
 * refetch — see the "Write reconciliation" note above for why an immediate
 * refetch would read GitHub's lagged list and undo the change. A no-op when the
 * list isn't cached yet (a mutation can't have fired without the list loaded).
 */
function patchEngineeringIssues(
  queryClient: QueryClient,
  update: (issues: EngineeringIssue[]) => EngineeringIssue[],
): void {
  queryClient.setQueryData<EngineeringResponse>(ENGINEERING_KEY, (prev) =>
    prev ? { ...prev, issues: update(prev.issues) } : prev,
  );
}

/** Create an engineering issue, then insert the returned issue at the top of
 *  the cached list (it's the newest-updated, so it belongs there). The create
 *  response is the authoritative projected issue; guard against a double-insert
 *  in case a reconciling poll already landed it. */
export function useCreateIssue() {
  const queryClient = useQueryClient();
  return useMutation<EngineeringIssue, Error, CreateIssueRequest>({
    mutationFn: (req) => apiPost<EngineeringIssue>("/api/engineering", req),
    onSuccess: (created) => {
      patchEngineeringIssues(queryClient, (issues) =>
        issues.some((i) => i.number === created.number)
          ? issues
          : [created, ...issues],
      );
    },
  });
}

/** Close an issue (mark done) and drop it from the cached open list. */
export function useCloseIssue() {
  const queryClient = useQueryClient();
  return useMutation<EngineeringIssue, Error, number>({
    mutationFn: (number) =>
      apiPost<EngineeringIssue>(`/api/engineering/${number}/close`, {}),
    onSuccess: (_closed, number) => {
      patchEngineeringIssues(queryClient, (issues) =>
        issues.filter((i) => i.number !== number),
      );
    },
  });
}

/** Toggle the pin (the `surfaced` float label) on an issue, reflecting the new
 *  label set in the cache so the row regroups (into / out of the Pinned group)
 *  immediately. */
export function useSetPinned() {
  const queryClient = useQueryClient();
  return useMutation<
    { ok: boolean; number: number; pinned: boolean },
    Error,
    { number: number; pinned: boolean }
  >({
    mutationFn: ({ number, pinned }) =>
      apiPost(`/api/engineering/${number}/pin`, { pinned }),
    onSuccess: (_data, { number, pinned }) => {
      patchEngineeringIssues(queryClient, (issues) =>
        issues.map((issue) => {
          if (issue.number !== number) return issue;
          const labels = pinned
            ? issue.labels.includes(ENGINEERING_PINNED_LABEL)
              ? issue.labels
              : [...issue.labels, ENGINEERING_PINNED_LABEL]
            : issue.labels.filter((l) => l !== ENGINEERING_PINNED_LABEL);
          return { ...issue, labels };
        }),
      );
    },
  });
}
